import React, {
  FC, useCallback, useContext, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { DataCollectorForm } from '../../models/DataCollectorForm';
import { DniNieCifCamera } from '../dni-nie-cif-camera';
import { IAInfoForm } from '../IAInfoForm';
import { IAScanDocumentContext } from '../scan-document-modal/IAScanDocumentContext';
import { DNINIEDataCollectorByPhotoComponent } from './types';
import Button from 'components/base/Button';
import Loader from 'components/base/Loader';

import { useSteps } from 'hooks/useSteps';
import { StepPositions } from 'hooks/useSteps/types';
import { Base64Data } from 'models/Base64Data';
import { MimeTypes } from 'models/Base64Data/MimeTypes';
import { useDniScanInfo } from 'modules/ia/hooks/useDniScanInfo';
import { useIA } from 'modules/ia/hooks/useIa';

import { showErrorToast } from 'utils/toasts';

import './index.scss';

export const DNINIEDataCollectorByPhoto: FC<DNINIEDataCollectorByPhotoComponent> = (
  { onConfirm, onCancel },
) => {
  const { documentType } = useContext(IAScanDocumentContext);
  const { t } = useTranslation();
  const { fetchInfoFromPicture, loading } = useIA();
  const [isFrontCameraActive, setIsFrontCameraActive] = useState<boolean>(false);

  const {
    setStep,
    isFirstStep: isTakeFrontPhotoStep,
    isSecondStep,
    isThirdStep: isReviewDataFormStep,
  } = useSteps();

  const [canAccept, setCanAccept] = useState<boolean>(false);
  const [selectedValues, setselectedValues] = useState<DataCollectorForm>();

  const [frontPicture, setFrontPicture] = useState<Base64Data>();
  const [backPicture, setBackPicture] = useState<Base64Data>();
  const {
    setDocInfo, docInfo, docScore, setDocScore,
  } = useDniScanInfo();

  const getInfo = useCallback(
    ({
      frontPicture: front,
      backPicture: back,
    }: { frontPicture: Base64Data; backPicture: Base64Data }) => {
      fetchInfoFromPicture(documentType, { backPicture: back, frontPicture: front })
        .then((response) => {
          setDocInfo(response.data);
          setDocScore(response.score);

          setStep(StepPositions.THIRD);

          return response;
        })
        .catch((e: Error) => {
          showErrorToast(e.message);

          setStep(StepPositions.THIRD);

          return false;
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <div className="document-dni-data-collector-component">
      {isTakeFrontPhotoStep
        && (
          <React.Fragment>
            <div className="step-header">{t('ia.front')}</div>
            <DniNieCifCamera
              isFrontCameraActive={isFrontCameraActive}
              onAccept={(picture) => {
                setFrontPicture(picture);
                setStep(StepPositions.SECOND);
              }}
              onIsFrontCameraUpdate={(v) => {
                setIsFrontCameraActive(v);
              }}
            />
          </React.Fragment>
        )}

      {isSecondStep
        && !loading
        && (
          <React.Fragment>
            <div className="step-header">{t('ia.back')}</div>
            <DniNieCifCamera
              isFrontCameraActive={isFrontCameraActive}
              onAccept={(picture) => {
                setBackPicture(picture);
                if (frontPicture) {
                  getInfo({ backPicture: picture, frontPicture });
                }
              }}
              onIsFrontCameraUpdate={(v) => {
                setIsFrontCameraActive(v);
              }}
            />
          </React.Fragment>
        )}

      {isSecondStep
        && loading
        && (
          <React.Fragment>
            <div className="step-header">{`${t('ia.scanningDocument')}`}</div>
            <Loader />
            <div
              className="summary-photo-preview"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              {frontPicture && (
                <img
                  alt="DNI front"
                  className="document-dni-data-collector-image"
                  src={frontPicture.toDataUrl()}
                />
              )}
              {backPicture && (
                <img
                  alt="DNI back"
                  className="document-dni-data-collector-image"
                  src={backPicture.toDataUrl()}
                />
              )}
            </div>
          </React.Fragment>
        )}

      {isReviewDataFormStep
        && (
          <React.Fragment>
            <div className="step-header">{t('ia.reviewData')}</div>
            <div
              className="summary-photo-preview"
              style={{
                display: 'flex',
                justifyContent: 'space-around',
              }}
            >
              {frontPicture && (
                <div className="document-dni-data-collector-image">
                  <div>{t('ia.front')}</div>
                  <img
                    alt="DNI front"
                    src={frontPicture.toDataUrl()}
                  />
                </div>
              )}
              {backPicture && (
                <div className="document-dni-data-collector-image">
                  <div>{t('ia.back')}</div>
                  <img
                    alt="DNI back"
                    src={backPicture.toDataUrl()}
                  />
                </div>
              )}
            </div>
            <IAInfoForm
              docInfo={docInfo}
              docScore={docScore}
              onChange={({ isValid, values }) => {
                setCanAccept(isValid);
                setselectedValues(values);
              }}
            />
            <div className="dni-data-collector-buttons">
              <Button
                className="photo-camera-button"
                text={t('common.cancel')}
                type="button"
                onClick={onCancel}
              />
              <Button
                className="photo-camera-button"
                disabled={!canAccept}
                text={t('common.accept')}
                type="button"
                onClick={() => {
                  if (selectedValues) {
                    onConfirm(
                      {
                        ...selectedValues,
                        backCapture: backPicture,
                        frontCapture: frontPicture,
                      },
                      MimeTypes.IMAGE_PNG,
                    );
                  }
                }}
              />
            </div>
          </React.Fragment>
        )}
    </div>
  );
};
